import { useCallback, useEffect, useRef, useState } from 'react'

export const useAvailableActivitiesSelector = ({
  availableActivities,
  selectedRowsIds: externalSelectedRowsIds,
  setSelectedRowsIds: externalSetSelectedRowsIds,
}) => {
  // Use external state if provided, otherwise use internal state
  const [internalSelectedRowsIds, internalSetSelectedRowsIds] = useState({})
  const selectedRowsIds = externalSelectedRowsIds ?? internalSelectedRowsIds
  const setSelectedRowsIds =
    externalSetSelectedRowsIds ?? internalSetSelectedRowsIds

  const hasInitialized = useRef(false)

  const getSelectedCount = useCallback(
    (selectedRows = selectedRowsIds) =>
      Object.entries(selectedRows)
        .filter(([_, isSelected]) => isSelected)
        .map(([key, _]) => key).length,
    [selectedRowsIds]
  )

  const handleSelect = useCallback(
    (updatedSelectedRowsIds) => {
      setSelectedRowsIds(updatedSelectedRowsIds)
    },
    [setSelectedRowsIds]
  )

  const handleSelectAll = useCallback(() => {
    const selected = Object.fromEntries(
      availableActivities.map((activity) => [activity.cmeActivityId, true])
    )
    setSelectedRowsIds(selected)
  }, [setSelectedRowsIds, availableActivities])

  const handleDeselectAll = useCallback(() => {
    setSelectedRowsIds({})
  }, [setSelectedRowsIds])

  // Auto-select all on first load only if no external state exists
  useEffect(() => {
    const isUsingInternalState = !externalSelectedRowsIds
    const shouldAutoSelectAll =
      isUsingInternalState ||
      Object.keys(externalSelectedRowsIds || {}).length === 0

    if (availableActivities.length > 0 && !hasInitialized.current) {
      if (shouldAutoSelectAll) {
        const selected = Object.fromEntries(
          availableActivities.map((activity) => [activity.cmeActivityId, true])
        )
        setSelectedRowsIds(selected)
      }
      hasInitialized.current = true
    }
  }, [availableActivities, externalSelectedRowsIds, setSelectedRowsIds])

  return {
    selectedRowsIds,
    getSelectedCount,
    handleSelect,
    handleSelectAll,
    handleDeselectAll,
  }
}
